/**
 * Filtering without a page load.
 *
 * The form works on its own: it is a GET form pointing at the collection, so
 * with the script off, ticking a box and submitting still filters. This only
 * removes the reload, by asking Shopify to re-render the section and swapping
 * the two parts of it that change.
 *
 * Every request carries an AbortController, because a person ticking three
 * boxes quickly starts three requests and the slowest one must not be the one
 * that lands.
 */
(() => {
  const RESULTS = '[data-filter-results]';
  const FILTERS = '[data-filter-form]';
  const CHIPS = '[data-active-filters]';
  // The count lives in the toolbar, not in the results it counts, so it has
  // to be swapped by name or it keeps reporting the unfiltered collection.
  const COUNT = '[data-filter-count]';

  const root = document.querySelector('[data-collection-filtering]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  let inFlight = null;
  let debounce = null;

  const urlFor = (form) => {
    const params = new URLSearchParams(new FormData(form));
    // an empty price field means "no bound", not "zero"
    [...params.keys()].forEach((k) => {
      if (params.get(k) === '') params.delete(k);
    });
    const query = params.toString();
    return { display: form.action + (query ? '?' + query : ''), query };
  };

  const render = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    [RESULTS, CHIPS, COUNT].forEach((selector) => {
      const next = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (next && current) current.replaceWith(next);
    });

    // Two filter forms live in the page, the sidebar and the phone's sheet,
    // and both have to be brought up to date. Replacing only the first left
    // whichever one was not on screen showing the previous selection.
    const fresh = doc.querySelectorAll(FILTERS);
    document.querySelectorAll(FILTERS).forEach((current, index) => {
      if (fresh[index]) current.replaceWith(fresh[index]);
    });

    wire();
  };

  const fetchInto = (display) => {
    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    const results = document.querySelector(RESULTS);
    if (results) results.setAttribute('aria-busy', 'true');

    const separator = display.includes('?') ? '&' : '?';
    fetch(`${display}${separator}section_id=${sectionId}`, { signal: inFlight.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then((html) => {
        history.pushState({ filtered: true }, '', display);
        render(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        window.location.href = display;
      });
  };

  const apply = (form, { push = true } = {}) => {
    const { display, query } = urlFor(form);

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    const results = document.querySelector(RESULTS);
    if (results) results.setAttribute('aria-busy', 'true');

    const separator = query ? '&' : '?';
    fetch(`${display}${separator}section_id=${sectionId}`, { signal: inFlight.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then((html) => {
        if (push) history.pushState({ filtered: true }, '', display);
        render(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Anything else: let the browser do it the ordinary way rather than
        // leaving the person looking at results that no longer match the form.
        window.location.href = display;
      });
  };

  function wire() {
    document.querySelectorAll(FILTERS).forEach(wireForm);

    // The chips above the results are links into the filtered view, so they
    // go through the same path rather than reloading.
    document.querySelectorAll('[data-filter-remove]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        fetchInto(link.getAttribute('href'));
      });
    });
  }

  function wireForm(form) {
    if (!form) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      apply(form);
    });

    form.addEventListener('change', (event) => {
      if (event.target.type === 'number') return; // handled on input, debounced
      apply(form);
    });

    form.addEventListener('input', (event) => {
      if (event.target.type !== 'number') return;
      clearTimeout(debounce);
      debounce = setTimeout(() => apply(form), 500);
    });

    form.querySelectorAll('[data-clear-filters]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        form.reset();
        form.querySelectorAll('input[type="checkbox"]').forEach((c) => (c.checked = false));
        form.querySelectorAll('input[type="number"]').forEach((n) => (n.value = ''));
        apply(form);
      });
    });
  }

  addEventListener('popstate', () => window.location.reload());
  wire();
})();
